import { computed, onBeforeUnmount, onMounted, shallowRef, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { enableMediaSoundPreference } from '@/composables/useMediaSoundPreference'
import {
  buildLegacyPlayerRouteQuery,
  getLegacyPlayerCurrentLocationQuery,
  getLegacyPlayerQueryString,
  hasLegacyPlayerSoundIntentCookie,
  LEGACY_PLAYER_OPEN_EVENT,
  LEGACY_PLAYER_QUERY_KEY,
  removeLegacyPlayerRouteQuery,
  resolveLegacyPlayerDirectUrl,
  resolveLegacyPlayerFrameUrl,
  shouldRenderLegacyPlayerBridgeMode,
  shouldOpenLegacyPlayerInOverlay,
  type LegacyPlayerOpenDetail,
} from '@/utils/legacy-player'

export function useLegacyPlayerOverlay() {
  const route = useRoute()
  const router = useRouter()
  const iframeLoadKey = shallowRef(0)
  const currentBridgeQuery = computed(() => ({
    ...getLegacyPlayerCurrentLocationQuery(),
    ...route.query,
  }))
  const normalizedPlayerUrl = computed(() =>
    resolveLegacyPlayerDirectUrl(
      getLegacyPlayerQueryString(currentBridgeQuery.value, LEGACY_PLAYER_QUERY_KEY),
    ),
  )
  const playerUrl = computed(() => resolveLegacyPlayerFrameUrl(normalizedPlayerUrl.value))
  const isOpen = computed(() => normalizedPlayerUrl.value !== null)
  const isBridgeMode = computed(() => shouldRenderLegacyPlayerBridgeMode(currentBridgeQuery.value))
  let soundIntentTimer: ReturnType<typeof window.setInterval> | null = null

  function syncLegacyPlayerSoundIntent() {
    if (hasLegacyPlayerSoundIntentCookie()) {
      enableMediaSoundPreference()
    }
  }

  function startSoundIntentSync() {
    if (soundIntentTimer !== null || typeof window === 'undefined') {
      return
    }

    syncLegacyPlayerSoundIntent()
    soundIntentTimer = window.setInterval(syncLegacyPlayerSoundIntent, 800)
  }

  function stopSoundIntentSync() {
    if (soundIntentTimer === null) {
      return
    }

    window.clearInterval(soundIntentTimer)
    soundIntentTimer = null
  }

  async function open(url: string) {
    const normalizedUrl = resolveLegacyPlayerDirectUrl(url)

    if (!normalizedUrl) {
      return
    }

    await router.push({
      path: route.path,
      query: buildLegacyPlayerRouteQuery(normalizedUrl, route.query),
      hash: route.hash,
    })
  }

  async function close() {
    if (!isOpen.value) {
      return
    }

    syncLegacyPlayerSoundIntent()
    stopSoundIntentSync()

    await router.replace({
      path: route.path,
      query: removeLegacyPlayerRouteQuery(route.query),
      hash: route.hash,
    })
  }

  function handleOpenEvent(event: CustomEvent<LegacyPlayerOpenDetail>) {
    if (
      !shouldOpenLegacyPlayerInOverlay(event.detail.url, {
        currentQuery: route.query,
      })
    ) {
      return
    }

    event.preventDefault()
    void open(event.detail.url)
  }

  watch(
    () => playerUrl.value,
    () => {
      iframeLoadKey.value += 1
    },
  )

  watch(
    isOpen,
    (open) => {
      if (open) {
        startSoundIntentSync()
        return
      }

      syncLegacyPlayerSoundIntent()
      stopSoundIntentSync()
    },
    { immediate: true },
  )

  onMounted(() => {
    window.addEventListener(LEGACY_PLAYER_OPEN_EVENT, handleOpenEvent)
  })

  onBeforeUnmount(() => {
    stopSoundIntentSync()
    window.removeEventListener(LEGACY_PLAYER_OPEN_EVENT, handleOpenEvent)
  })

  return {
    isOpen,
    isBridgeMode,
    playerUrl,
    iframeLoadKey,
    close,
  }
}
